import { v1, toResult } from '@/infrastructure/api/v1/client';
import { updateMyOrgMember } from '@/infrastructure/clients/orgsApi';
import { safeParseJson } from '@/utils/safeParseJson';

/**
 * Session-scoped cache + in-flight dedupe for `org_members.member_preferences`
 * lookups. Many editor components (TopMenu, EditorPage, LibrarySection,
 * TutorialModal, AssetSearchModal, etc.) each fire their own copy of this
 * query — caching it once avoids repeated GETs and the resulting 429s.
 *
 * The cached value is the raw JSONB blob (snake_case keys) so callers that
 * read dynamic fields like `zoom_sensitivity` keep working.
 *
 * Writes go through `patchMemberPreferences` (or `queueMemberPreferencesWrite`
 * for another endpoint that changes the blob), which runs them one at a time
 * and drops the cache afterwards.
 */
type Preferences = Record<string, any>;

const cache = new Map<string, Preferences>();
const inflight = new Map<string, Promise<Preferences>>();
// Bumped by every invalidation, so a read that started before it is not cached after it
const generation = new Map<string, number>();
// The blob last handed to a reader, which is what a caller of updateMemberPreferences spread
const lastRead = new Map<string, Preferences>();
const writes = new Map<string, Promise<unknown>>();

async function readPreferences(): Promise<Preferences> {
  const { data, error } = await toResult(v1.GET('/api/v1/orgs/me/member'));
  if (error) throw error;
  return safeParseJson<Preferences>(data.memberPreferences) || {};
}

/** The signed-in user's preferences; `userId` keys the cache and must be that user's id. */
export async function getMemberPreferences(userId: string): Promise<Preferences> {
  const cached = cache.get(userId);
  if (cached !== undefined) {
    return cached;
  }

  const existing = inflight.get(userId);
  if (existing) {
    return existing;
  }

  const startedAt = generation.get(userId) ?? 0;
  const promise = readPreferences()
    .then(prefs => {
      if ((generation.get(userId) ?? 0) === startedAt) {
        cache.set(userId, prefs);
        lastRead.set(userId, prefs);
      }
      return prefs;
    })
    .catch(error => {
      console.error('Error fetching member preferences:', error);
      throw error;
    })
    .finally(() => {
      if (inflight.get(userId) === promise) inflight.delete(userId);
    });

  inflight.set(userId, promise);
  return promise;
}

/** Replace the cached preferences for a user, when the new value is known. */
export function setMemberPreferences(
  userId: string,
  preferences: Preferences
): void {
  cache.set(userId, preferences);
  lastRead.set(userId, preferences);
}

/**
 * Drop a cached entry (or the whole cache). Call when the new preferences
 * value is not known at update time, or on logout / user switch.
 */
export function invalidateMemberPreferences(userId?: string): void {
  if (userId) {
    cache.delete(userId);
    inflight.delete(userId);
    generation.set(userId, (generation.get(userId) ?? 0) + 1);
  } else {
    cache.clear();
    inflight.clear();
    for (const key of generation.keys()) generation.set(key, (generation.get(key) ?? 0) + 1);
    lastRead.clear();
  }
}

export function queueMemberPreferencesWrite<T>(userId: string, write: () => Promise<T>): Promise<T> {
  const run = (writes.get(userId) ?? Promise.resolve())
    .then(write)
    .finally(() => invalidateMemberPreferences(userId));
  const settled = run.catch(() => undefined);
  writes.set(userId, settled);
  void settled.then(() => {
    if (writes.get(userId) === settled) writes.delete(userId);
  });
  return run;
}

// The server replaces the whole blob, so the patch goes onto a fresh read; a failed read writes nothing
export function patchMemberPreferences(userId: string, patch: Preferences): Promise<void> {
  return queueMemberPreferencesWrite(userId, async () => {
    const current = await readPreferences();
    const next = { ...current, ...patch };
    await updateMyOrgMember({ member_preferences: next });
    lastRead.set(userId, next);
  });
}

const sameValue = (a: unknown, b: unknown) => a === b || JSON.stringify(a) === JSON.stringify(b);

export function changedPreferences(userId: string, preferences: Preferences): Preferences {
  const basis = lastRead.get(userId);
  if (!basis) return preferences;
  return Object.fromEntries(Object.entries(preferences).filter(([key, value]) => !sameValue(value, basis[key])));
}
